import { createPinia } from "pinia";
import { createApp } from "vue";

import { useSessionStore } from "@/entities/session";
import { i18n } from "@/shared/i18n";

import App from "./App.vue";
import { installAuthShell, installObservability } from "./providers";
import { createAppRouter } from "./router";
import "./styles/globals.css";

/**
 * Composition root (ADR-0003 D2, ADR-0006 D2′/D4 — app-шар, Amendment A1).
 *
 * Порядок: createApp → installObservability → pinia → i18n → session →
 * router → installAuthShell → hydrateOptimistic → mount →
 * fire-and-forget background revalidation.
 *
 * Hint-path (resume after iOS memory discard): hydrateOptimistic читає
 * sessionStorage, status='authenticated' → shell малюється миттєво, guard
 * вирішує синхронно → revalidate() перевіряє сесію у фоні.
 *
 * Cold-path (no hint, fresh load): status='idle' → guard викликає
 * whenBootstrapped() (single-flight) → bootstrap() з skipAuthRedirect →
 * App.vue spinner до resolve.
 */
async function boot(): Promise<void> {
  const app = createApp(App);
  installObservability({ app });

  const pinia = createPinia();
  app.use(pinia);
  app.use(i18n);

  const session = useSessionStore(pinia);
  const router = createAppRouter();
  installAuthShell({ router, session });

  session.hydrateOptimistic();

  app.use(router);
  app.mount("#app");

  // Fire-and-forget background revalidation (ADR-0006 D2′):
  // hint-path → revalidate (без skipAuthRedirect, 401 → D4-seam);
  // cold-path → whenBootstrapped (single-flight, guard теж його awaits).
  if (session.isAuthenticated) {
    void session.revalidate();
  } else {
    void session.whenBootstrapped();
  }
}

void boot();
